/**
 * Image crop — which part of a picture an image element shows.
 *
 * `el.crop = { x, y, w, h }` is the SOURCE rectangle, in fractions of the natural
 * image, mapped onto the element's picture box (its box minus padding and border).
 * No `crop` → the element draws with `fit` + `objectPosition`, exactly as before.
 *
 * Values outside 0…1 are legal and mean "the box reaches past the picture's edge" —
 * which is precisely what `fit: contain` looks like. That is what lets crop mode
 * open on any image without a single pixel moving: the crop is seeded from what the
 * fit mode already draws (see `cropFromFit`).
 *
 * The canvas preview (ElementRenderer), the PNG/PDF export (renderDocumentToCanvas)
 * and the crop overlay (CanvasViewport) all read the rect through the helpers here,
 * so the three can't drift apart.
 */

/** The whole picture, stretched onto the box. */
export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };

/** A crop can shrink to a sliver but never to nothing (it divides). */
const MIN_CROP = 0.005;

export function isCrop(crop) {
    return !!crop
        && Number.isFinite(crop.x) && Number.isFinite(crop.y)
        && Number.isFinite(crop.w) && Number.isFinite(crop.h)
        && crop.w > 0 && crop.h > 0;
}

/** The element's crop, or null — the one gate every renderer asks. */
export function getCrop(el) {
    return isCrop(el?.crop) ? el.crop : null;
}

/** Four decimals is ~0.01% of a picture: exact enough, and keeps saved JSON small. */
export function roundCrop(crop) {
    const r = (n) => Math.round(n * 1e4) / 1e4;
    return {
        x: r(crop.x),
        y: r(crop.y),
        w: r(Math.max(MIN_CROP, crop.w)),
        h: r(Math.max(MIN_CROP, crop.h)),
    };
}

/** Padding + border — the inset between the element box and the picture. */
export function elementInsets(styles = {}) {
    const p = Number(styles.padding) || 0;
    const bw = Number(styles.borderWidth) || 0;
    return {
        top: (Number(styles.paddingTop ?? p) || 0) + bw,
        right: (Number(styles.paddingRight ?? p) || 0) + bw,
        bottom: (Number(styles.paddingBottom ?? p) || 0) + bw,
        left: (Number(styles.paddingLeft ?? p) || 0) + bw,
    };
}

/** The picture box in ELEMENT-LOCAL coordinates (0,0 = the element's top-left). */
export function innerRect(el) {
    const i = elementInsets(el?.styles);
    return {
        x: i.left,
        y: i.top,
        w: Math.max(1, (Number(el?.width) || 0) - i.left - i.right),
        h: Math.max(1, (Number(el?.height) || 0) - i.top - i.bottom),
    };
}

/** The anchor `objectPosition` names, as 0…1 fractions. */
export function positionAnchor(position = 'center') {
    const parts = String(position || 'center').trim().toLowerCase().split(/\s+/);
    let ax = 0.5;
    let ay = 0.5;
    parts.forEach((p) => {
        if (p === 'left') ax = 0;
        else if (p === 'right') ax = 1;
        else if (p === 'top') ay = 0;
        else if (p === 'bottom') ay = 1;
    });
    return { ax, ay };
}

function fitScale(mode, w, h, iw, ih) {
    if (mode === 'cover') return Math.max(w / iw, h / ih);
    if (mode === 'none') return 1;
    if (mode === 'scale-down') return Math.min(1, Math.min(w / iw, h / ih));
    return Math.min(w / iw, h / ih); // contain / fit
}

/**
 * The crop that reproduces a CSS fit mode pixel for pixel.
 *
 * One formula covers contain / cover / none / scale-down: the picture is drawn at
 * `scale`, so the box sees `boxW / scale` source pixels, anchored by objectPosition.
 * `fill` stretches both axes independently — that IS the whole picture, so it is the
 * full rect.
 */
export function cropForFitMode(el, natural, fitMode) {
    const iw = Number(natural?.w) || 0;
    const ih = Number(natural?.h) || 0;
    if (!iw || !ih) return { ...FULL_CROP };

    const mode = fitMode === 'fit' ? 'contain' : (fitMode || 'contain');
    if (mode === 'fill') return { ...FULL_CROP };

    const box = innerRect(el);
    const { ax, ay } = positionAnchor(el?.styles?.objectPosition);
    const scale = fitScale(mode, box.w, box.h, iw, ih) || 1;
    const sw = box.w / scale;
    const sh = box.h / scale;
    return roundCrop({
        x: ((iw - sw) * ax) / iw,
        y: ((ih - sh) * ay) / ih,
        w: sw / iw,
        h: sh / ih,
    });
}

/** Seed for crop mode: what this element's own `fit` already shows. */
export function cropFromFit(el, natural) {
    return cropForFitMode(el, natural, el?.fit || 'contain');
}

/**
 * CSS that maps the crop rect onto the picture box.
 *
 * The image is blown up to `1 / crop.w` of the box and pulled back by the crop's
 * origin, so the requested source rect lands exactly on the box — the DOM twin of
 * the export's drawImage(sx, sy, sw, sh, …). `maxWidth: none` matters: a global
 * `img { max-width: 100% }` would otherwise silently cap the blow-up.
 */
export function cropImageStyle(crop) {
    return {
        position: 'absolute',
        left: `${(-crop.x / crop.w) * 100}%`,
        top: `${(-crop.y / crop.h) * 100}%`,
        width: `${(1 / crop.w) * 100}%`,
        height: `${(1 / crop.h) * 100}%`,
        maxWidth: 'none',
        maxHeight: 'none',
        objectFit: 'fill',
    };
}

/** Where the WHOLE picture sits, in element-local coordinates. */
export function pictureRect(el, crop) {
    const box = innerRect(el);
    const w = box.w / crop.w;
    const h = box.h / crop.h;
    return { x: box.x - crop.x * w, y: box.y - crop.y * h, w, h };
}

/** The inverse: the crop for a picture parked at `picture`, seen through `box`. */
export function cropFromPicture(box, picture) {
    return roundCrop({
        x: (box.x - picture.x) / picture.w,
        y: (box.y - picture.y) / picture.h,
        w: box.w / picture.w,
        h: box.h / picture.h,
    });
}

/**
 * Zoom the picture about the centre of the crop: `s > 1` shows fewer source pixels,
 * so the picture looks bigger inside the same frame.
 */
export function scaleCrop(crop, s) {
    const k = Math.min(50, Math.max(0.02, Number(s) || 1));
    const w = Math.max(MIN_CROP, crop.w / k);
    const h = Math.max(MIN_CROP, crop.h / k);
    return roundCrop({
        x: crop.x + crop.w / 2 - w / 2,
        y: crop.y + crop.h / 2 - h / 2,
        w,
        h,
    });
}

/** Slide the crop over the picture (dragging the picture moves it the other way). */
export function moveCrop(crop, dx, dy) {
    return roundCrop({ x: crop.x + dx, y: crop.y + dy, w: crop.w, h: crop.h });
}

/**
 * Natural pixel size of an image source, memoised.
 *
 * Crop maths is expressed in fractions of the source, so seeding a crop from a fit
 * mode needs the real dimensions. Only metadata is read (never pixels), so this
 * works on cross-origin images without CORS. A source that fails to load resolves
 * to null rather than rejecting — the caller falls back to the full rect.
 */
const naturalCache = new Map();

export function naturalSize(src) {
    if (!src) return Promise.resolve(null);
    const cached = naturalCache.get(src);
    if (cached) return cached;
    const pending = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
        img.onerror = () => resolve(null);
        img.src = src;
    });
    naturalCache.set(src, pending);
    return pending;
}
